<template>
  <canvas id="chart" ref="chart" class="chart" height="100" />
</template>

<script>
import { Chart, registerables } from 'chart.js';
import { storeToRefs } from 'pinia';
import { useScreensizeStore } from '@stores/useScreensizeStore.ts';
import { eventsMixin } from '@elements/events';
import { translationMixin } from '@elements/translation';
import { requestsMixin } from '@tools/requests-mixin';
import { URL_OFFER_STATISTICS } from '@tools/routes/routes';
import { Logger } from '@tools/logger/logger';

Chart.register(...registerables);

export default {
  mixins: [ eventsMixin, translationMixin, requestsMixin ],
  setup() {
    const {
      isMobileOrTablet,
      isDesktop,
      isTabletLarge,
    } = storeToRefs(useScreensizeStore());

    return {
      isMobileOrTablet,
      isDesktop,
      isTabletLarge,
      hashId: document.getElementById('offer-statistics').dataset.offer,
    };
  },
  data() {
    return {
      mobileLimit: 7,
      desktopLimit: 30,
      color: 'rgb(255, 212, 204)',
      ctx: null,
      chart: null,
      chartLabels: [],
      chartData: [],
    };
  },
  mounted() {
    this.ctx = this.$refs.chart;
    this.get((URL_OFFER_STATISTICS.replace('{hashid}', this.hashId)))
      .then((response) => response.data)
      .then((json) => {
        const data = json.message;
        this.createChart(data.labels, data.values);
        window.addEventListener('resize', this.debouncedUpdateChart);
      }).catch(Logger.error);
  },
  methods: {
    createChart(initialLabels, initialData) {
      this.chartLabels = initialLabels;
      this.chartData = initialData;
      const labels = (this.isDesktop || this.isTabletLarge) ? initialLabels : this.truncate(initialLabels);
      const data = (this.isDesktop || this.isTabletLarge) ? initialData : this.truncate(initialData);
      const transchoiceMethod = this.transchoice;
      this.chart = new Chart(this.ctx, {
        type: 'bar',
        data: {
          labels,
          datasets: [
            {
              label: this.trans('statistics.charts.views_number'),
              data,
              backgroundColor: Array(labels).fill(this.color),
            },
          ],
        },
        options: {
          scales: {
            x: {
              beginAtZero: true,
            },
            y: {
              ticks: {
                beginAtZero: true,
                callback(value) {
                  return (value % 1 === 0) ? value : null;
                },
              },
            },
          },
          plugins: {
            tooltip: {
              callbacks: {
                label(context) {
                  const nbViews = context.dataset.data[context.dataIndex];
                  return `${transchoiceMethod('statistics.charts.views', { count: nbViews })}`;
                },
              },
            },
          },
        },
      });
    },
    debouncedUpdateChart() {
      this.debounce(this.updateChart());
    },
    updateChart() {
      if ((this.isMobileOrTablet) && this.chart.data.labels.length > this.mobileLimit) {
        this.chart.data.labels = this.truncate(this.chart.data.labels);
        this.chart.data.datasets[0].data = this.truncate(this.chart.data.datasets[0].data);
      } else if ((this.isDesktop || this.isTabletLarge) && this.chart.data.labels.length < this.desktopLimit) {
        this.chart.data.labels = this.chartLabels;
        this.chart.data.datasets[0].data = this.chartData;
      }
      this.chart.update();
    },
    truncate(array) {
      return array.slice(Math.max(array.length - 7, 0));
    },
  },
};
</script>
